import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { AxiosError } from "axios";
import { ActivePromotionCycleInterface, PromotionRequest } from "@utils/types";

export interface CycleHistoryState {
  state: "failed" | "success" | "loading" | "idle";
  requestState: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  promotionRequests: PromotionRequest[];
  errorMessage: string | null;
  inactivePromotionCycle: ActivePromotionCycleInterface[] | null;
  selectedCycleId: number | null;
}

const initialState: CycleHistoryState = {
  state: "idle",
  requestState: "idle",
  stateMessage: null,
  errorMessage: null,
  promotionRequests: [],
  inactivePromotionCycle: null,
  selectedCycleId: null,
};

export const getInactivePromotionCycles = createAsyncThunk(
  "cycleHistory/getInactivePromotionCycles",
  async (_, { dispatch }) => {
    return new Promise<{
      inactivePromotionCycle: ActivePromotionCycleInterface[];
    }>((resolve, reject) => {
      APIService.getInstance()
        .get<{
          promotionCycles: ActivePromotionCycleInterface[];
        }>(
          AppConfig.serviceUrls.getInactivePromotionCycle
        )
        .then((resp) => {
          resolve({
            inactivePromotionCycle: resp.data.promotionCycles,
          });
        })
        .catch((error: AxiosError) => {
          dispatch(
            enqueueSnackbarMessage({
              message:
                error.response?.status === 500
                  ? String(error.response?.data)
                  : "Unable to load promotion cycles",
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

export const getAllPromotionRequests = createAsyncThunk(
  "cycleHistory/getAllPromotionRequests",
  async (cycleId: number, { dispatch }) => {
    return new Promise<{
      promotionRequests: PromotionRequest[];
    }>((resolve, reject) => {
        APIService.getInstance().get<{
            promotionRequests: PromotionRequest[];
        }>(
            AppConfig.serviceUrls.retrieveAllPromotionRequests +
            "?cycleId=" +
            cycleId
        )
        .then(async (req) => {
            const requests = req.data.promotionRequests;
            
            const historyUrls = requests.map((r) => {
                return (
                    AppConfig.serviceUrls.getEmployeeHistory +
                    "?employeeWorkEmail=" +
                    r.employeeEmail
                );
            });

            const historyResponses = await Promise.all(
                historyUrls.map((url) =>
                    APIService.getInstance()
                    .get(url)
                    .catch((err) => ({ error: true }))
                )
            );

            requests.forEach((request, index) => {
                const history = historyResponses[index] as any;

                if (
                    history &&
                    !history.error &&
                    history.data &&
                    history.data.employeeInfo
                ) {
                    request.lastPromotedDate =
                    history.data.employeeInfo.lastPromotedDate ?? "N/A";
                    request.joinDate =
                    history.data.employeeInfo.startDate ?? "N/A";
                    request.location =
                    history.data.employeeInfo.joinedLocation ?? "N/A";
                } else {
                    request.lastPromotedDate = "<error>";
                    request.joinDate = "<error>";
                    request.location = "<error>";
                }
            });
            resolve({
                promotionRequests: requests,
            });            
        })
        .catch((error: AxiosError) => {
            dispatch(
                enqueueSnackbarMessage({
                message:
                    error.response?.status === 500
                    ? String(error.response?.data)
                    : "Unable to load promotion requests",
                type: "error",
                })
            );
            reject(error);
        });
    })}
);

export const getAllFLPromotionApplications = createAsyncThunk(
  "cycleHistory/getAllFLPromotionApplications",
  async (cycleId: number, { dispatch }) => {
    return new Promise<{
      promotionRequests: PromotionRequest[];
    }>((resolve, reject) => {
        APIService.getInstance().get<{
            promotionRequests: PromotionRequest[];
        }>(
            AppConfig.serviceUrls.retrieveFLAllPromotionApplications +
                "&cycleId=" +
                cycleId
        )
        .then(async (req) => {
            const requests = req.data.promotionRequests;
            
            const historyUrls = requests.map((r) => {
                return (
                    AppConfig.serviceUrls.getEmployeeHistory +
                    "?employeeWorkEmail=" +
                    r.employeeEmail
                );
            });

            const historyResponses = await Promise.all(
                historyUrls.map((url) =>
                    APIService.getInstance()
                    .get(url)
                    .catch((err) => ({ error: true }))
                )
            );

            requests.forEach((request, index) => {
                const history = historyResponses[index] as any;

                if (
                    history &&
                    !history.error &&
                    history.data &&
                    history.data.employeeInfo
                ) {
                    request.lastPromotedDate =
                    history.data.employeeInfo.lastPromotedDate ?? "N/A";
                    request.joinDate =
                    history.data.employeeInfo.startDate ?? "N/A";
                    request.location =
                    history.data.employeeInfo.joinedLocation ?? "N/A";
                } else {
                    request.lastPromotedDate = "<error>";
                    request.joinDate = "<error>";
                    request.location = "<error>";
                }
            });
            resolve({
                promotionRequests: requests,
            });            
        })
        .catch((error: AxiosError) => {
            dispatch(
                enqueueSnackbarMessage({
                message:
                    error.response?.status === 500
                    ? String(error.response?.data)
                    : "Unable to load promotion applications",
                type: "error",
                })
            );
            reject(error);
        });
    })}
);

export const CycleHistory = createSlice({
  name: "promotion-cycle-history",
  initialState,
  reducers: {
    reset: (state) => {
      state.state = "idle";
      state.requestState = "idle";
      state.stateMessage = "";
      state.inactivePromotionCycle =  null;
      state.promotionRequests = [];
      state.selectedCycleId =  null;
    },
    setStateMessage: (state, action: PayloadAction<string>) => {
      state.stateMessage = action.payload;
    },
    setSelectedCycleID: (state, action) => {
      state.selectedCycleId = action.payload;   
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInactivePromotionCycles.pending, (state, action) => {
        state.stateMessage = "Loading Promotion Requests";
        state.state = "loading";
      })
      .addCase(getInactivePromotionCycles.fulfilled, (state, action) => {
        state.inactivePromotionCycle = action.payload.inactivePromotionCycle;
        state.state = "success";
      })
      .addCase(getInactivePromotionCycles.rejected, (state, action) => {
        state.state = "failed";
      })

      .addCase(getAllPromotionRequests.pending, (state, action) => {
        state.stateMessage = "Loading Promotion Requests";
        state.requestState = "loading";
      })
      .addCase(getAllPromotionRequests.fulfilled, (state, action) => {
        state.promotionRequests = action.payload.promotionRequests;
        state.requestState = "success";
      })
      .addCase(getAllPromotionRequests.rejected, (state, action) => {
        state.requestState = "failed";
      })

      .addCase(getAllFLPromotionApplications.pending, (state, action) => {
        state.stateMessage = "Loading Promotion Requests";
        state.requestState = "loading";
      })
      .addCase(getAllFLPromotionApplications.fulfilled, (state, action) => {
        state.promotionRequests = action.payload.promotionRequests;
        state.requestState = "success";
      })
      .addCase(getAllFLPromotionApplications.rejected, (state, action) => {
        state.requestState = "failed";
      })
  },
});

export const { reset, setStateMessage, setSelectedCycleID } = CycleHistory.actions;

export default CycleHistory.reducer;
